import type { LocalDate } from '../dates';

export type LedgerKind = 'expense' | 'income' | 'transfer' | 'refund';

/** The ledger fields that counting and breakdowns read. apps/web maps Drizzle rows onto this. */
export interface LedgerEntryLike {
  id: string;
  date: LocalDate;
  amountMinor: number;
  kind: LedgerKind;
  categoryId: string | null;
  method: string | null;
  sourceModule: string;
  note: string | null;
  deletedAt: string | null;
}

/** Inclusive on both ends. */
export interface DateRange {
  start: LocalDate;
  end: LocalDate;
}

export function inRange(date: LocalDate, range: DateRange): boolean {
  // 'YYYY-MM-DD' strings sort the same way as the days they name.
  return date >= range.start && date <= range.end;
}

/** Spec §7: expense and refund count in Spending; transfer never does; income is not spending. */
export function countsInSpending(e: Pick<LedgerEntryLike, 'kind' | 'deletedAt'>): boolean {
  return e.deletedAt === null && (e.kind === 'expense' || e.kind === 'refund');
}

export function spendAmount(
  e: Pick<LedgerEntryLike, 'kind' | 'amountMinor' | 'deletedAt'>,
): number {
  if (!countsInSpending(e)) return 0;
  return e.kind === 'refund' ? -e.amountMinor : e.amountMinor;
}

export function spendingIn<T extends LedgerEntryLike>(
  entries: readonly T[],
  range: DateRange,
): T[] {
  return entries.filter((e) => countsInSpending(e) && inRange(e.date, range));
}

export function totalSpent(entries: readonly LedgerEntryLike[], range: DateRange): number {
  return spendingIn(entries, range).reduce((sum, e) => sum + spendAmount(e), 0);
}
